import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { DetailField } from '@/components/shared/detail-field';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api-errors';
import { api } from '@/lib/api';
import {
  canEditCoupon,
  couponDiscountLabel,
  couponDurationLabel,
  couponUsesLabel,
  type PlatformCoupon,
} from '@/lib/coupon';
import { formatCalendarDate, formatDateTime, formatMoney } from '@/utils/format';

const BILLING_CURRENCY = 'GHS';

export const CouponDetailDialog = ({
  coupon,
  open,
  onOpenChange,
  canManage,
  onEdit,
}: {
  coupon: PlatformCoupon | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canManage: boolean;
  onEdit?: (coupon: PlatformCoupon) => void;
}) => {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const toggleCoupon = useMutation({
    mutationFn: (input: { id: string; active: boolean }) =>
      api.patch<PlatformCoupon>(`/platform/billing/coupons/${input.id}`, { active: input.active }),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['platform-coupons'] });
      toast.success(
        variables.active ? t('billing.coupons.activated') : t('billing.coupons.deactivated'),
      );
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const close = () => onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent closeLabel={tCommon('action.close')} size="md">
        <DialogHeader>
          <DialogTitle>{coupon?.code ?? t('billing.coupons.detailTitle')}</DialogTitle>
          <DialogDescription>{t('billing.coupons.detailDescription')}</DialogDescription>
        </DialogHeader>

        <DialogBody contentClassName="space-y-3">
          {coupon ? (
            <dl className="space-y-3">
              <DetailField label={t('billing.coupons.code')}>{coupon.code}</DetailField>
              <DetailField label={t('billing.coupons.kind')}>
                {t(`billing.coupons.kinds.${coupon.kind}`)}
              </DetailField>
              <DetailField label={t('billing.coupons.assignedChurch')}>
                {coupon.assignedChurchName ?? t('billing.coupons.assignedChurchAny')}
              </DetailField>
              <DetailField label={t('billing.coupons.discountType')}>
                {t(`billing.coupons.discountTypes.${coupon.discountType}`)}
              </DetailField>
              <DetailField label={t('billing.coupons.discountValue')}>
                {couponDiscountLabel(coupon, t, (minor) => formatMoney(minor, BILLING_CURRENCY))}
              </DetailField>
              <DetailField label={t('billing.coupons.duration')}>
                {couponDurationLabel(coupon, t)}
              </DetailField>
              <DetailField label={t('billing.coupons.redemptions')}>
                {couponUsesLabel(coupon, t)}
              </DetailField>
              <DetailField label={t('billing.coupons.status')}>
                <Badge variant={coupon.active ? 'success' : 'neutral'}>
                  {coupon.active ? t('billing.coupons.active') : t('billing.coupons.inactive')}
                </Badge>
              </DetailField>
              <DetailField label={t('billing.coupons.notes')}>
                {coupon.notes?.trim() ? coupon.notes : t('billing.coupons.emptyValue')}
              </DetailField>
              <DetailField label={t('billing.coupons.validFrom')}>
                {coupon.validFrom ? formatCalendarDate(coupon.validFrom) : t('billing.coupons.emptyValue')}
              </DetailField>
              <DetailField label={t('billing.coupons.validUntil')}>
                {coupon.validUntil ? formatCalendarDate(coupon.validUntil) : t('billing.coupons.emptyValue')}
              </DetailField>
              <DetailField label={t('billing.coupons.createdAt')}>
                {formatDateTime(coupon.createdAt)}
              </DetailField>
              <DetailField label={t('billing.coupons.updatedAt')}>
                {formatDateTime(coupon.updatedAt)}
              </DetailField>
            </dl>
          ) : (
            <p className="text-body-sm text-fg-secondary">{t('billing.coupons.detailMissing')}</p>
          )}
        </DialogBody>

        <DialogFooter className="flex justify-end gap-2">
          <Button variant="outline" onClick={close}>
            {tCommon('action.close')}
          </Button>
          {canManage && coupon ? (
            <Button
              variant="outline"
              loading={toggleCoupon.isPending}
              onClick={() => toggleCoupon.mutate({ id: coupon.id, active: !coupon.active })}
            >
              {coupon.active ? t('billing.coupons.deactivate') : t('billing.coupons.activate')}
            </Button>
          ) : null}
          {canManage && coupon && onEdit && canEditCoupon(coupon) ? (
            <Button type="button" onClick={() => onEdit(coupon)}>
              {t('billing.coupons.edit')}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
